import ApiClient from "@/services/api/apiClient";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import {
  ApiError,
  extractApiErrorMessage,
  getErrorStatusCode,
} from "@/services/api/apiError";
import type {
  Ec2InstanceStatusData,
  StartStopEc2ResponseData,
} from "./ec2Instance.types";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// The system endpoints return the payload flat (ApiClient passes object bodies
// through as-is), but tolerate an optional { success, data } envelope.
function unwrapData<T>(
  responseData: unknown,
  fallback: string,
  isValid: (payload: Record<string, unknown>) => boolean,
): T {
  const body = isRecord(responseData) ? responseData : {};
  const payload = isRecord(body.data) ? body.data : body;
  if (body.success === false || !isValid(payload)) {
    throw new ApiError(
      typeof body.message === "string" ? body.message : fallback,
      getErrorStatusCode(responseData),
      responseData,
    );
  }
  return payload as T;
}

const hasInstanceState = (payload: Record<string, unknown>) =>
  typeof payload.state === "string" || typeof payload.stateCode === "number";

const isNonEmpty = (payload: Record<string, unknown>) =>
  Object.keys(payload).length > 0;

export const systemService = {
  async getEc2InstanceStatus(): Promise<Ec2InstanceStatusData> {
    try {
      const response = await ApiClient.get<unknown>(
        API_ENDPOINTS.SYSTEM.EC2_INSTANCE_STATUS,
        undefined,
      );
      return unwrapData<Ec2InstanceStatusData>(
        response as unknown,
        "Failed to load EC2 instance status",
        hasInstanceState,
      );
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        extractApiErrorMessage(error),
        getErrorStatusCode(error),
        error,
      );
    }
  },

  async startStopEc2Instance(
    action: "start" | "stop",
  ): Promise<StartStopEc2ResponseData> {
    try {
      const response = await ApiClient.post<unknown>(
        API_ENDPOINTS.SYSTEM.START_STOP_EC2_INSTANCE,
        { action },
      );
      return unwrapData<StartStopEc2ResponseData>(
        response as unknown,
        `Failed to ${action} EC2 instance`,
        isNonEmpty,
      );
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        extractApiErrorMessage(error),
        getErrorStatusCode(error),
        error,
      );
    }
  },
};
